/** The one action an empty state offers - KD's review of 2026-11-02.
 *
 * An empty place says what will appear and offers **one** way to start: a verb that is the same
 * control as the page header's Add ("Add vendor", "Upload a bill"). It is drawn the way the header's
 * primary action is (`ActionButton`'s primary weight), as a button that does the same thing or a link
 * to where it is done.
 *
 * **A role that may not use it does not get it.** `allowed` false draws the one-sentence reason in
 * its place ("An Admin adds sites."), not a disabled button: the role could not use the control any
 * other way, so a disabled one is a control that predictably fails (UX-04), and the sentence says
 * whose act this is. An unknown role (the session read still in flight) draws nothing at all.
 */

import { Plus } from "@phosphor-icons/react";
import { Link } from "@tanstack/react-router";

import button from "./ActionButton.module.css";
import styles from "./EmptyAction.module.css";

export interface EmptyActionProps {
  label: string;
  /** Whether the signed-in role may do it. `undefined` (role not read yet) draws nothing. */
  allowed: boolean | undefined;
  /** What is said instead of the button when the role may not: one short sentence. */
  reason?: string;
  /** The button's act, or... */
  onClick?: () => void;
  /** ...the place it is done, as a link carrying the reader's search (window, scope-2 basis). */
  to?: string;
  search?: unknown;
  /** A plus before the verb, for an Add. Default on; a link to a screen passes `false`. */
  plus?: boolean;
  testId?: string;
  /** The reason's own test id, where one is already named (`no-period-who-can`). Default: `${testId}-reason`. */
  reasonTestId?: string;
}

export function EmptyAction({
  label,
  allowed,
  reason,
  onClick,
  to,
  search,
  plus = true,
  testId = "empty-action",
  reasonTestId,
}: EmptyActionProps) {
  if (allowed === undefined) return null;
  if (!allowed) {
    return reason ? (
      <span className={styles.reason} data-testid={reasonTestId ?? `${testId}-reason`}>
        {reason}
      </span>
    ) : null;
  }
  const glyph = plus ? <Plus size={14} weight="bold" aria-hidden="true" /> : null;
  if (to) {
    return (
      <Link
        to={to}
        search={search as never}
        className={`${button.primary} ${styles.link}`}
        data-testid={testId}
      >
        {glyph}
        {label}
      </Link>
    );
  }
  return (
    <button type="button" className={button.primary} onClick={onClick} data-testid={testId}>
      {glyph}
      {label}
    </button>
  );
}
